import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Package, Heart, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import BundleCustomizer from '../components/BundleCustomizer';
import TestimonialCard from '../components/TestimonialCard';
import { pricingPackages, uses, sustainabilityPoints } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = ({ cart, addToCart }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState('duo');
  const [showBundleCustomizer, setShowBundleCustomizer] = useState(false);
  const [currentBundle, setCurrentBundle] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, settingsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/products/`),
        axios.get(`${API}/settings/`),
        axios.get(`${API}/testimonials/?featured_only=false`)
      ]);
      setProducts(productsRes.data);
      setSettings(settingsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const isInCart = (productId) => {
    return cart.some(item => item.id === productId && !item.bundleId);
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg.id);
    setCurrentBundle(pkg);
    setShowBundleCustomizer(true);
  };

  const handleBundleConfirm = (bundleData) => {
    const { package: pkg, products: selectedProducts } = bundleData;
    
    // Create a bundle item
    const bundleId = `bundle_${Date.now()}`;
    const bundleItem = {
      id: bundleId,
      bundleId: bundleId,
      name: pkg.name,
      package: pkg,
      products: selectedProducts,
      price: pkg.price,
      quantity: 1,
      isBundle: true
    };

    addToCart(bundleItem);
    setShowBundleCustomizer(false);
    toast.success(`${pkg.name} added to cart!`);
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section" style={settings?.hero_banner_image ? {
        backgroundImage: `linear-gradient(rgba(250, 255, 238, 0.9), rgba(250, 255, 238, 0.95)), url(${settings.hero_banner_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}}>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <Leaf size={16} />
              <span>Sustainably Scented Wellness</span>
            </div>
            <h1 className="hero-title">
              {settings?.hero_title || 'From Waste to Wellness'}
            </h1>
            <p className="hero-subtitle">
              {settings?.hero_subtitle || 'Eco-friendly scented camphor pouches made from upcycled footwear factory fabric, infused with pure essential oils'}
            </p>
            <div className="hero-actions">
              <button 
                className="btn-primary btn-cta"
                onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Scents
              </button>
              <button 
                className="btn-secondary"
                onClick={() => document.getElementById('why-us').scrollIntoView({ behavior: 'smooth' })}
              >
                Our Story
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="products-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Wellness in Every Scent</h2>
            <p className="section-subtitle">
              Discover our collection of therapeutic essential oil blends, each crafted for specific wellness benefits
            </p>
          </div>
          {loading ? (
            <div className="loading-state">Loading products...</div>
          ) : (
            <div className="products-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={addToCart}
                  isInCart={isInCart(product.id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Choose Your Bundle</h2>
            <p className="section-subtitle">Save more when you buy in sets - customize each bundle with your favorite scents</p>
          </div>
          <div className="pricing-grid">
            {pricingPackages.map((pkg) => (
              <Card 
                key={pkg.id} 
                className={`pricing-card ${pkg.popular ? 'popular' : ''} ${selectedPackage === pkg.id ? 'selected' : ''}`}
                onClick={() => setSelectedPackage(pkg.id)}
              >
                {pkg.popular && <div className="popular-badge">Most Popular</div>}
                <CardContent className="pricing-content">
                  <h3 className="pricing-name">{pkg.name}</h3>
                  <div className="pricing-price">
                    <span className="price-currency">₹</span>
                    <span className="price-value">{pkg.price}</span>
                  </div>
                  <p className="pricing-quantity">{pkg.quantity} {pkg.quantity === 1 ? 'Pouch' : 'Pouches'}</p>
                  {pkg.savings > 0 && (
                    <div className="pricing-savings">Save ₹{pkg.savings}</div>
                  )}
                  <button 
                    className="btn-primary" 
                    style={{ width: '100%', marginTop: '16px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePackageSelect(pkg);
                    }}
                  >
                    Customize Bundle
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sole to Soul Section */}
      <section id="why-us" className="why-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Sole to Soul?</h2>
            <p className="section-subtitle">
              Every pouch tells a story of sustainability and wellness
            </p>
          </div>
          <div className="sustainability-grid">
            {sustainabilityPoints.map((point, index) => (
              <div key={index} className="sustainability-card">
                <div className="sustainability-icon">{point.icon}</div>
                <h3 className="sustainability-title">{point.title}</h3>
                <p className="sustainability-description">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Uses Section */}
      <section id="uses" className="uses-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perfect For Every Space</h2>
            <p className="section-subtitle">
              Versatile wellness solutions for your daily life
            </p>
          </div>
          <div className="uses-grid">
            {uses.map((use) => (
              <Card key={use.id} className="use-card">
                <div className="use-image-wrapper">
                  <img src={use.image} alt={use.title} className="use-image" />
                  <div className="use-overlay">
                    <div className="use-icon">{use.icon}</div>
                  </div>
                </div>
                <CardContent className="use-content">
                  <h3 className="use-title">{use.title}</h3>
                  <p className="use-description">{use.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="instagram-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Follow Our Journey</h2>
            <p className="section-subtitle">
              Join our community for wellness tips and behind-the-scenes stories
            </p>
          </div>
          <div className="instagram-cta">
            <a 
              href={settings?.instagram_url || "https://www.instagram.com/soletosoul_27"}
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary btn-cta"
            >
              <Sparkles size={20} />
              Follow @soletosoul_27
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="testimonials-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">What Our Customers Say</h2>
              <p className="section-subtitle">
                Real experiences from our wellness community
              </p>
            </div>
            <div className="testimonials-grid">
              {testimonials.slice(0, 6).map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section id="contact" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <Heart size={48} className="cta-icon" />
            <h2 className="cta-title">Gifts That Smell Good and Do Good</h2>
            <p className="cta-subtitle">
              Mindful gifts for mindful living. Start your wellness journey today.
            </p>
            <button 
              className="btn-primary btn-cta"
              onClick={() => navigate('/cart')}
            >
              View Cart {cart.length > 0 && `(${cart.length})`}
            </button>
          </div>
        </div>
      </section>

      {/* Bundle Customizer Modal */}
      {showBundleCustomizer && currentBundle && (
        <BundleCustomizer
          bundlePackage={currentBundle}
          products={products}
          onConfirm={handleBundleConfirm}
          onClose={() => setShowBundleCustomizer(false)}
        />
      )}
    </div>
  );
};

export default Home;